import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

type UseCoursesUrlFiltersProps = {
  defaultSort?: string;
  queryParams?: string;
};

const useCoursesUrlFilters = ({
  defaultSort = 'alphabetic',
  queryParams = ''
}: UseCoursesUrlFiltersProps) => {
  const router = useRouter();

  const ready = useRef<boolean>(false);

  const [allQueryParams, setAllQueryParams] = useState(
    new URLSearchParams(queryParams)
  );

  useEffect(() => {
    ready.current = false;

    // Remove the 'page' query parameter from the URL when it's not needed
    const newQueryParams = new URLSearchParams(queryParams);
    newQueryParams.sort();

    const currentPage = newQueryParams.get('page') || 1;

    // Reset to page 1 when any query parameter except 'page' changes
    allQueryParams.sort();
    allQueryParams.delete('page');

    newQueryParams.delete('page');

    if (
      currentPage !== 1 &&
      newQueryParams.toString() !== allQueryParams.toString()
    ) {
      router.replace(
        { query: { ...Object.fromEntries(newQueryParams), page: 1 } },
        undefined,
        { shallow: true }
      );

      newQueryParams.set('page', '1');
    } else newQueryParams.set('page', currentPage.toString());

    setAllQueryParams(newQueryParams);
    ready.current = true;
  }, [queryParams]);

  const onPageChange = ({ page }: { page: number }) => {
    router.push(
      { query: { ...Object.fromEntries(allQueryParams), page } },
      undefined,
      { shallow: true }
    );
  };

  /**
   * Get the API URL with the current query parameters
   * @param baseUrl - The base URL to append the query parameters to
   * @param limitPerPage - The number of items per page
   * @returns The URL with the current query parameters
   */
  const getUrl = (baseUrl: string, limitPerPage: number): string => {
    const [basePath, existingQuery] = baseUrl.split('?');
    const params = new URLSearchParams(existingQuery || '');

    allQueryParams.forEach((value, key) => {
      params.set(key, value);
    });

    params.set('sortBy', allQueryParams.get('sortBy') || defaultSort);
    params.set('page', allQueryParams.get('page') || '1');
    params.set('limit', limitPerPage.toString());
    // Needed by the client, but isn't needed for the API URL
    params.delete('type');

    return `${basePath}?${params.toString()}`;
  };

  return {
    ready: ready.current,
    currentPage: Number(allQueryParams.get('page')) || 1,
    getUrl,
    onPageChange
  };
};

export default useCoursesUrlFilters;
